import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CourseInformation from "../components/CourseInformation";
import { api } from "../lib/api";

export default function StudyCourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  return <StudyCourseView key={JSON.stringify([courseId, user?.id, user?.tenant_id])} />;
}

function StudyCourseView() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const pending = useRef(false);
  const [shortlisting, setShortlisting] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [messageError, setMessageError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setCourse((await api.get(`/study/catalogue/${encodeURIComponent(courseId)}`)).data); }
    catch (e) { setCourse(null); setError(e?.response?.status === 404 ? "This course is unavailable or is not published." : "The course could not be loaded. Please try again."); }
    finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const shortlist = async () => {
    if (pending.current || shortlisted) return;
    pending.current = true; setShortlisting(true); setMessage(""); setMessageError(false);
    try {
      await api.post("/study/shortlists", { provider_id: course.institution_id || course.institution_name,
        provider_name: course.institution_name, country: course.country || "Not specified",
        course_id: course.id, course_title: course.programme_name });
      setShortlisted(true); setMessage("Course added to your shortlist.");
    } catch (e) { setMessageError(true); setMessage(applicationErrorMessage(e, "Sign in to save this course.")); }
    finally { pending.current = false; setShortlisting(false); }
  };

  if (loading) return <main className="mx-auto max-w-5xl px-5 py-12"><p role="status">Loading course details…</p></main>;
  if (error || !course) return <main className="mx-auto max-w-5xl px-5 py-12" data-testid="course-not-found"><h1 className="text-2xl font-semibold">Course unavailable</h1><p role="alert" className="mt-3 text-slate-600">{error || "This course could not be found."}</p><button className="btn btn-ghost mt-6 mr-3" onClick={load}>Retry loading</button><Link className="btn btn-primary mt-6" to="/study">Back to Study catalogue</Link></main>;

  const selected = encodeURIComponent(course.id);
  return <main className="mx-auto max-w-5xl px-5 py-12" data-testid="course-detail-page">
    <Link className="text-sm text-blue-800 underline" to="/study">← Back to Study catalogue</Link>
    <article className="card mt-6 p-6 sm:p-8">
      <p className="overline">{course.institution_name}</p>
      <h1 className="mt-2 text-3xl font-semibold">{course.programme_name}</h1>
      <p className="mt-3 text-slate-600">{[course.city, course.country].filter(Boolean).join(", ") || "Location not specified"}</p>
      <CourseInformation course={course} />
      {message && <p className={messageError ? "mt-5 text-sm text-red-700" : "mt-5 text-sm text-blue-900"} role={messageError ? "alert" : "status"}>{message}</p>}
      <div className="mt-7 flex flex-wrap gap-3">
        <Link className="btn btn-primary" to={`/study/workspace?courseId=${selected}&mode=enquiry`}>Enquire about this course</Link>
        <Link className="btn btn-primary" to={`/study/workspace?courseId=${selected}&mode=application`}>Apply for this course</Link>
        <button className="btn btn-ghost" type="button" disabled={shortlisting || shortlisted} onClick={shortlist}>{shortlisting ? "Shortlisting…" : shortlisted ? "Course shortlisted" : "Shortlist this course"}</button>
        {course.official_url && <a className="btn btn-ghost" href={course.official_url} target="_blank" rel="noreferrer">Official course source</a>}
      </div>
    </article>
  </main>;
}
